/**
 * Phase 6b2 PR 3 — `useTeamKudos` hook.
 *
 * Fetches the kudos feed for the signed-in user's active team and
 * exposes a `sendKudo` write that POSTs to the existing vanilla
 * endpoint at `/api/team/kudos`. Server stamps `from_user_id =
 * req.authUser.id` (safety gate #6) — the client never passes the
 * sender id.
 *
 * Endpoint surface (from `api/index.js:4683-4742`):
 *
 *   POST  /api/team/kudos
 *     body: { teamId, toUserId, kudoType, message? }
 *     returns: { kudo: <row> }
 *
 *   GET   /api/team/kudos?teamId=<id>
 *     returns: { kudos: [{ id, kudoType, message, fromName, toName,
 *                           fromUserId, toUserId, createdAt }] }
 *     filtered to last 30 days, sorted DESC, limit 50
 *
 * T3 — `viewed_at` is now a real column on team_kudos. This hook
 * exposes it as `viewedAt` on each row, and adds a `markViewed`
 * batch endpoint wrapper that the feed component fires on first
 * render to stamp the recipient's unviewed kudos. The mark-viewed
 * write is optimistic with timestamp-matched rollback so a parallel
 * tab's success doesn't get stomped on this tab's failure.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';
import { isKudoTypeKey, MAX_KUDO_MESSAGE_LENGTH, type KudoTypeKey } from '../lib/kudoTypes';

export interface KudoFeedRow {
  id: string;
  /** Persisted enum key — narrowed by `isKudoTypeKey` for safety. */
  kudoType: KudoTypeKey;
  /** Optional 140-char body. Vanilla stores '' (empty string) for
   *  no-message kudos, not NULL, per the NOT NULL schema. */
  message: string;
  fromUserId: string;
  toUserId: string;
  fromName: string;
  toName: string;
  /** ISO 8601 timestamp. */
  createdAt: string;
  /** ISO 8601 timestamp when this kudo's recipient first opened
   *  /v2/team and the row was batch-marked viewed. NULL until then
   *  — that NULL is the signal the feed uses to fire the receive-
   *  side animation exactly once. */
  viewedAt: string | null;
}

export interface SendKudoInput {
  teamId: string;
  toUserId: string;
  kudoType: KudoTypeKey;
  /** Optional. Server slices to 140 chars regardless; we cap
   *  client-side so the UI live-counter and the persisted value
   *  agree. */
  message?: string;
}

export interface UseTeamKudosArgs {
  /** When null, the hook stays in `loading: false, data: null`
   *  (no team → no feed to fetch). */
  teamId: string | null;
}

export interface UseTeamKudosResult {
  data: KudoFeedRow[] | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Insert a new kudo. Returns the saved row's id (server stamps
   *  from_user_id). On network/server error, throws. The composer
   *  catches + surfaces inline. */
  sendKudo: (input: SendKudoInput) => Promise<string>;
  /** Batch mark-viewed for the recipient — fires once when the feed
   *  first renders. Optimistic with timestamp-matched rollback:
   *  - applies a local optimistic timestamp only to rows currently
   *    `viewedAt === null`;
   *  - on success snaps to server timestamp (or rolls back if
   *    server reports `viewedAt: null` meaning all were already
   *    viewed);
   *  - on error rolls back only rows whose value still equals the
   *    optimistic timestamp (a parallel tab's success isn't stomped).
   *  Empty ids array is a no-op. */
  markViewed: (ids: string[]) => Promise<void>;
}

interface ApiGetResponse {
  kudos?: Array<{
    id: string;
    kudoType: string;
    message: string | null;
    fromName: string;
    toName: string;
    fromUserId: string;
    toUserId: string;
    createdAt: string;
    viewedAt?: string | null;
  }>;
  error?: string;
}

interface ApiSendResponse {
  kudo?: { id: string };
  error?: string;
}

interface ApiMarkViewedResponse {
  updated?: number;
  viewedAt?: string | null;
  error?: string;
}

async function readJsonError(res: Response): Promise<never> {
  let body: { error?: string } = {};
  try {
    body = (await res.json()) as { error?: string };
  } catch {
    /* not JSON */
  }
  throw new Error(body?.error ?? `${res.status} ${res.statusText || 'Request failed'}`);
}

export function useTeamKudos({ teamId }: UseTeamKudosArgs): UseTeamKudosResult {
  const { loading: authLoading } = useAuthContext();
  const fetchAuthed = useAuthFetch();

  const [data, setData] = useState<KudoFeedRow[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (authLoading || !teamId) {
      // Don't query while auth resolves; reset to empty when no team.
      if (!teamId) {
        setData(null);
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAuthed(
        `/api/team/kudos?teamId=${encodeURIComponent(teamId)}`,
      );
      if (!res.ok) await readJsonError(res);
      const json = (await res.json()) as ApiGetResponse;
      const rows = (json.kudos ?? [])
        .filter((k) => isKudoTypeKey(k.kudoType))
        .map<KudoFeedRow>((k) => ({
          id: k.id,
          kudoType: k.kudoType as KudoTypeKey,
          message: k.message ?? '',
          fromUserId: k.fromUserId,
          toUserId: k.toUserId,
          fromName: k.fromName,
          toName: k.toName,
          createdAt: k.createdAt,
          viewedAt: k.viewedAt ?? null,
        }));
      setData(rows);
      setLoading(false);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setLoading(false);
    }
  }, [authLoading, teamId, fetchAuthed]);

  useEffect(() => {
    void load();
  }, [load]);

  const sendKudo = useCallback<UseTeamKudosResult['sendKudo']>(
    async (input) => {
      const message = (input.message ?? '').slice(0, MAX_KUDO_MESSAGE_LENGTH);
      const res = await fetchAuthed('/api/team/kudos', {
        method: 'POST',
        body: JSON.stringify({
          teamId: input.teamId,
          toUserId: input.toUserId,
          kudoType: input.kudoType,
          message,
        }),
      });
      if (!res.ok) await readJsonError(res);
      const json = (await res.json()) as ApiSendResponse;
      if (!json.kudo?.id) throw new Error('Send succeeded but server returned no id');
      return json.kudo.id;
    },
    [fetchAuthed],
  );

  const markViewed = useCallback<UseTeamKudosResult['markViewed']>(
    async (ids) => {
      if (!ids || ids.length === 0) return;

      // Snapshot the optimistic stamp ONCE so we can match it during
      // rollback. A parallel tab's success will produce a different
      // stamp, so we never accidentally clear its value.
      const optimisticAt = new Date().toISOString();

      // Optimistic apply — only on rows where viewedAt is currently
      // null. Already-stamped rows stay as-is.
      setData((prev) =>
        prev === null
          ? prev
          : prev.map((r) =>
              ids.includes(r.id) && r.viewedAt === null
                ? { ...r, viewedAt: optimisticAt }
                : r,
            ),
      );

      try {
        const res = await fetchAuthed('/api/team/kudos/viewed', {
          method: 'POST',
          body: JSON.stringify({ kudoIds: ids }),
        });
        if (!res.ok) await readJsonError(res);
        const json = (await res.json()) as ApiMarkViewedResponse;
        const serverAt = json.viewedAt ?? null;
        if (serverAt !== null) {
          // Snap rows still showing optimistic stamp to the server's
          // canonical timestamp.
          setData((prev) =>
            prev === null
              ? prev
              : prev.map((r) =>
                  ids.includes(r.id) && r.viewedAt === optimisticAt
                    ? { ...r, viewedAt: serverAt }
                    : r,
                ),
          );
        } else {
          // Server says updated=0 (all were already viewed at fetch
          // time). Roll back the optimistic stamp on rows whose value
          // still matches — leaves other tabs' writes intact.
          setData((prev) =>
            prev === null
              ? prev
              : prev.map((r) =>
                  ids.includes(r.id) && r.viewedAt === optimisticAt
                    ? { ...r, viewedAt: null }
                    : r,
                ),
          );
        }
      } catch {
        // Rollback ONLY rows whose value still matches the optimistic
        // stamp we wrote. Rows that diverged (a parallel write
        // succeeded) stay as-is. Deliberately swallows the error —
        // this is a fire-and-forget marker; the next page load will
        // retry naturally since viewed_at IS NULL on the server.
        setData((prev) =>
          prev === null
            ? prev
            : prev.map((r) =>
                ids.includes(r.id) && r.viewedAt === optimisticAt
                  ? { ...r, viewedAt: null }
                  : r,
              ),
        );
      }
    },
    [fetchAuthed],
  );

  return { data, loading, error, refetch: load, sendKudo, markViewed };
}
